// src/hooks/useAHCREvent.ts

import { useCallback, useState } from 'react'
import { ahcrEventApiService } from '../api/ahcr-event.api'
import {
  AHCREvent,
  AHCREventCountResponse,
  AHCREventQueryParams,
} from '../types/ahcr-event.types'
import { useLoading } from 'layout/context/loading-context'
interface UseAHCREventHookReturn {
  // List operations
  ahcrEvents: AHCREvent[]
  totalAhcrEvents: number
  error: Error | null

  // Actions
  fetchAHCREvents: (params?: AHCREventQueryParams) => Promise<void>
  fetchAHCREventsCount: (
    params?: AHCREventQueryParams
  ) => Promise<AHCREventCountResponse | undefined>
}

export const useAHCREvent = (): UseAHCREventHookReturn => {
  const { startLoading, stopLoading } = useLoading()

  const [ahcrEvents, setAhcrEvents] = useState<AHCREvent[]>([])
  const [totalAhcrEvents, setTotalAhcrEvents] = useState<number>(0)
  const [error, setError] = useState<Error | null>(null)

  const fetchAHCREvents = useCallback(
    async (params?: AHCREventQueryParams): Promise<void> => {
      // Only proceed if params has clientId
      if (!params?.clientId) {
        return
      }

      startLoading('fetchAHCREvents')
      setError(null)

      try {
        const response = await ahcrEventApiService.getANPREvents(params)

        setAhcrEvents(response.responseObject)
        setTotalAhcrEvents(response.responseObject.length)
      } catch (err) {
        setError(err as Error)
        setAhcrEvents([])
      } finally {
        stopLoading('fetchAHCREvents')
      }
    },
    [startLoading, stopLoading]
  )
  const fetchAHCREventsCount = useCallback(
    async (
      params?: AHCREventQueryParams
    ): Promise<AHCREventCountResponse | undefined> => {
      if (!params?.clientId) {
        return
      }

      startLoading('fetchAHCREvents')
      setError(null)

      try {
        const response = await ahcrEventApiService.getAHCREventsCountByDate(
          params
        )

        return response.responseObject
      } catch (err) {
        setError(err as Error)
        setAhcrEvents([])
      } finally {
        stopLoading('fetchAHCREvents')
      }
    },
    [startLoading, stopLoading]
  )

  return {
    ahcrEvents,
    totalAhcrEvents,
    error,
    fetchAHCREvents,
    fetchAHCREventsCount,
  }
}
